import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Landmark, Building2, CheckCircle2, FileText, BadgeIndianRupee, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { LeadForm } from "../components/LeadForm";
import { EMICalculator } from "../components/EMICalculator";
import { FAQSection } from "../components/FAQSection";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { LenderCard } from "../components/LenderCard";
import { LENDERS, PERSONAL_LENDERS, BUSINESS_LENDERS, OVERDRAFT_LENDERS } from "../data/lenders";

const ICONS = { Landmark, Building2 };
const PRODUCT_PATH = { personal: "/personal-loan", business: "/business-loan", overdraft: "/overdraft-loan" };
const PRODUCT_LABEL = { personal: "Personal Loan", business: "Business Loan", overdraft: "Flexi Overdraft Loan" };
const RELATED = { personal: PERSONAL_LENDERS, business: BUSINESS_LENDERS, overdraft: OVERDRAFT_LENDERS };

export default function LenderDetailPage({ product }) {
  const { slug } = useParams();
  const lender = LENDERS[slug];
  if (!lender || !lender[product]) return <Navigate to={PRODUCT_PATH[product]} replace/>;
  const p = lender[product];
  const Icon = ICONS[lender.icon] || Landmark;

  const fees = [
    ["Processing Fee", p.procFee || "Up to 2.5%"],
    ["Prepayment", "Nil after 6 months (lender discretion)"],
    ["Late Payment", "₹500 – ₹1,500 per instance"],
    ["Loan Cancellation", "₹1,000 + GST"],
  ];

  const related = RELATED[product].filter(s => s !== slug).slice(0, 3);

  return (
    <div data-testid={`lender-page-${slug}`}>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#EAF5FD] via-white to-white">
        <div className="ol-container py-10 lg:py-14">
          <Breadcrumbs items={[
            { label: PRODUCT_LABEL[product], to: PRODUCT_PATH[product] },
            { label: lender.name }
          ]}/>
          <div className="grid lg:grid-cols-12 gap-10 items-start mt-6">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ background: `${lender.color}15` }}>
                  <Icon size={30} style={{ color: lender.color }}/>
                </div>
                <div>
                  <Badge className="bg-[#EAF5FD] text-[#1088D4] hover:bg-[#EAF5FD] border-0">RBI Registered</Badge>
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading text-[#273B59] mt-2">{lender.name} {PRODUCT_LABEL[product]}</h1>
                </div>
              </div>
              <p className="mt-5 text-lg text-[#475569] max-w-xl">Get a {PRODUCT_LABEL[product]} from {lender.name} with competitive rates, fast approval, and minimal paperwork.</p>
              <div className="mt-6 grid grid-cols-3 gap-3">
                <Stat label="Interest Rate" value={p.rate} highlight/>
                <Stat label="Max Amount" value={p.maxAmount}/>
                <Stat label="Tenure" value={p.tenure}/>
              </div>
              <div className="mt-6 flex gap-3">
                <Link to={`/apply?lender=${slug}&product=${product}`}><Button data-testid="lender-apply-btn" size="lg" className="ol-btn-primary h-11">Apply Now <ArrowRight size={14} className="ml-1.5"/></Button></Link>
                <Link to={PRODUCT_PATH[product]}><Button data-testid="lender-compare-btn" size="lg" variant="outline" className="h-11 border-[#273B59] text-[#273B59]">Compare More</Button></Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <LeadForm defaultType={product} lender={slug} title={`Apply with ${lender.short}`} source={`lender-${slug}`}/>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="ol-section">
        <div className="ol-container grid md:grid-cols-3 gap-8">
          <LenderOverview lender={lender} p={p} product={product} fees={fees} />
          <LenderAside lender={lender} p={p} slug={slug} product={product} />
        </div>
      </section>

      {/* EMI Calc */}
      <section className="bg-[#F7F9FC] ol-section">
        <div className="ol-container">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold font-heading text-[#273B59]">{lender.short} EMI Calculator</h2>
          </div>
          <EMICalculator productLabel={lender.short}/>
        </div>
      </section>

      <FAQSection/>

      {/* Related */}
      <section className="ol-section">
        <div className="ol-container">
          <h2 className="text-2xl sm:text-3xl font-bold font-heading text-[#273B59]">Related Offers</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-5">
            {related.map((s, i) => (
              <LenderCard key={s} slug={s} product={product} basePath={PRODUCT_PATH[product]} index={i}/>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

const Stat = ({ label, value, highlight }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-lg p-3 text-center">
    <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{label}</div>
    <div className={`mt-1 text-sm font-bold ${highlight ? "text-[#16A34A]" : "text-[#273B59]"}`}>{value}</div>
  </div>
);

const LenderOverview = ({ lender, p, product, fees }) => {
  const features = [
    `Interest rate ${p.rate}`,
    `Maximum amount ${p.maxAmount}`,
    `Tenure ${p.tenure}`,
    `Processing fee ${p.procFee || "Up to 2.5%"}`,
    `100% digital application`,
    `Disbursal within 24-72 hours for eligible applicants`,
  ];
  const eligibility = [
    product === "business" ? "Business vintage 2+ years" : "Age 21-60 years",
    product === "business" ? "Annual turnover ₹40 Lakh+" : "Salaried with min income ₹25,000/month",
    "CIBIL score 700+",
    "Indian resident",
  ];
  const docs = ["PAN & Aadhaar", "Income proof (Salary slips / ITR)", "Bank statements (3-12 months)", "Address proof", "Photograph"];

  return (
    <div className="md:col-span-2">
      <h2 className="text-2xl sm:text-3xl font-bold font-heading text-[#273B59]">Overview</h2>
      <p className="mt-4 text-[#475569] leading-relaxed">
        {lender.name} is one of India's most trusted financial institutions, offering {PRODUCT_LABEL[product].toLowerCase()} products designed for working professionals, entrepreneurs and businesses across India. With a focus on digital-first lending, competitive interest rates, and fast disbursal, {lender.short} is a popular choice on OneLoan.
      </p>

      <h3 className="mt-8 text-xl font-bold font-heading text-[#273B59]">Key Features</h3>
      <ul className="mt-4 space-y-2">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-[#273B59]"><CheckCircle2 size={16} className="text-[#16A34A] mt-0.5" />{f}</li>
        ))}
      </ul>

      <h3 className="mt-8 text-xl font-bold font-heading text-[#273B59]">Fees & Charges</h3>
      <div className="mt-4 border border-[#E2E8F0] rounded-lg overflow-hidden">
        {fees.map(([k, v], i) => (
          <div key={k} className={`grid grid-cols-2 px-4 py-3 text-sm ${i % 2 === 0 ? "bg-white" : "bg-[#F7F9FC]"}`}>
            <span className="text-[#475569]">{k}</span>
            <span className="font-medium text-[#273B59]">{v}</span>
          </div>
        ))}
      </div>

      <h3 className="mt-8 text-xl font-bold font-heading text-[#273B59]">Eligibility</h3>
      <ul className="mt-4 space-y-2">
        {eligibility.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-[#273B59]"><BadgeIndianRupee size={16} className="text-[#1088D4] mt-0.5" />{f}</li>)}
      </ul>

      <h3 className="mt-8 text-xl font-bold font-heading text-[#273B59]">Documents Required</h3>
      <ul className="mt-4 space-y-2">
        {docs.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-[#273B59]"><FileText size={16} className="text-[#1088D4] mt-0.5" />{f}</li>
        ))}
      </ul>
    </div>
  );
};

const LenderAside = ({ lender, p, slug, product }) => (
  <aside className="md:sticky md:top-24 h-fit">
    <div className="bg-[#EAF5FD] border border-[#1088D4]/20 rounded-2xl p-6">
      <h3 className="font-bold text-[#273B59] font-heading">Quick Apply with {lender.short}</h3>
      <p className="mt-2 text-xs text-[#475569]">Pre-approved offers in under 2 minutes.</p>
      <Link to={`/apply?lender=${slug}&product=${product}`}><Button data-testid="aside-apply-btn" className="w-full mt-4 ol-btn-primary h-11">Apply Now <ArrowRight size={14} className="ml-1" /></Button></Link>
      <div className="mt-4 pt-4 border-t border-[#1088D4]/15 text-xs space-y-2 text-[#273B59]">
        <div className="flex justify-between"><span className="text-[#475569]">Rate</span><span className="font-semibold">{p.rate}</span></div>
        <div className="flex justify-between"><span className="text-[#475569]">Max</span><span className="font-semibold">{p.maxAmount}</span></div>
        <div className="flex justify-between"><span className="text-[#475569]">Tenure</span><span className="font-semibold">{p.tenure}</span></div>
      </div>
    </div>
  </aside>
);
